import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  AccountTree,
  AttachMoney,
  CheckCircle,
  Schedule,
  Warning,
  ErrorOutline,
  HomeWork,
  BugReport,
  VpnKey,
  TrendingUp,
} from "@mui/icons-material";
import {
  useGetProTrackDashboardQuery,
  useGetPortfolioQuery,
} from "../../../redux/protrackApi";

const NAVY = "#123456";

function StatCard({
  icon,
  label,
  value,
  color,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  color?: string;
  sub?: React.ReactNode;
}) {
  return (
    <Card sx={{ flex: "1 1 150px", minWidth: 140 }}>
      <CardContent
        sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5 }}
      >
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            bgcolor: `${color ?? NAVY}18`,
            color: color ?? NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700} color={color}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export const ProTrackDashboard: React.FC = () => {
  const { data, isLoading } = useGetProTrackDashboardQuery();
  const { data: portfolio } = useGetPortfolioQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return null;

  const { milestones, snags, handovers, blockSummary } = data;
  const milestoneCompletion =
    milestones.total > 0
      ? Math.round((milestones.completed / milestones.total) * 100)
      : 0;

  const numFmt = (v: number) =>
    new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 0 }).format(v);

  return (
    <Box>
      {/* ── CTC Portfolio summary ── */}
      {portfolio && portfolio.totals.projects > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} color={NAVY} mb={2}>
            CTC Portfolio Overview
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
            <StatCard
              icon={<AccountTree />}
              label="Active Projects"
              value={portfolio.totals.projects}
              color={NAVY}
            />
            <StatCard
              icon={<TrendingUp />}
              label="Total CTC Value"
              value={`$${numFmt(portfolio.totals.totalCtc)}`}
              color="#1565c0"
              sub="across all sites"
            />
            <StatCard
              icon={<CheckCircle />}
              label="Work Done"
              value={`$${numFmt(portfolio.totals.totalWorkDone)}`}
              color="#2e7d32"
            />
            <StatCard
              icon={<AttachMoney />}
              label="Total Sales"
              value={`$${numFmt(portfolio.totals.totalSales)}`}
              color="#2e7d32"
            />
            <StatCard
              icon={<ErrorOutline />}
              label="Outstanding Debtors"
              value={`$${numFmt(portfolio.totals.totalOutstandingDebtors)}`}
              color={
                portfolio.totals.totalOutstandingDebtors > 0
                  ? "#c62828"
                  : undefined
              }
            />
            <StatCard
              icon={<AttachMoney />}
              label="NOSTRO (USD)"
              value={`$${numFmt(portfolio.totals.totalNostroUsd)}`}
              color="#1565c0"
            />
          </Box>

          {/* Site comparison */}
          <Typography variant="h6" fontWeight={700} color={NAVY} mb={2}>
            Site Comparison
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
            {portfolio.sites
              .filter((s) => s.projectCount > 0)
              .map((s) => (
                <Card key={s.site} sx={{ flex: "1 1 200px", minWidth: 180 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography fontWeight={700} color={NAVY}>
                        {s.site}
                      </Typography>
                      <Chip
                        label={`${s.projectCount} proj`}
                        size="small"
                        sx={{ fontSize: 10, height: 20 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Construction
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={s.avgConstructionPct}
                      sx={{
                        mb: 0.5,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": { bgcolor: NAVY },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {s.avgConstructionPct}% avg &nbsp;|&nbsp; Sales: $
                      {numFmt(s.totalSales)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
          </Box>
        </>
      )}

      {/* ── KPI row ── */}
      <Typography variant="h6" fontWeight={700} color={NAVY} mb={2}>
        Programme Overview
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2} mb={4}>
        <StatCard
          icon={<CheckCircle />}
          label="Milestones Complete"
          value={`${milestones.completed}/${milestones.total}`}
          color="#2e7d32"
          sub={`${milestoneCompletion}% done`}
        />
        <StatCard
          icon={<Schedule />}
          label="In Progress"
          value={milestones.inProgress}
          color={NAVY}
        />
        <StatCard
          icon={<Warning />}
          label="Delayed"
          value={milestones.delayed}
          color={milestones.delayed > 0 ? "#f57c00" : undefined}
        />
        <StatCard
          icon={<ErrorOutline />}
          label="Critical Snags"
          value={snags.critical}
          color={snags.critical > 0 ? "#c62828" : undefined}
          sub={`${snags.open} open total`}
        />
        <StatCard
          icon={<BugReport />}
          label="Open Snags"
          value={snags.open}
          color={snags.open > 0 ? "#e65100" : undefined}
          sub={`${snags.resolved} resolved`}
        />
        <StatCard
          icon={<VpnKey />}
          label="Keys Handed"
          value={handovers.keysHanded}
          color="#1565c0"
          sub={`${handovers.total} units total`}
        />
        <StatCard
          icon={<HomeWork />}
          label="Signed Off"
          value={handovers.signedOff}
          color="#2e7d32"
        />
      </Box>

      {/* ── Per-block summary ── */}
      {blockSummary.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} color={NAVY} mb={2}>
            Project Blocks
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {blockSummary.map((b) => (
              <Card key={b.blockId} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                    mb={1}
                  >
                    <Box>
                      <Typography fontWeight={700} color={NAVY}>
                        {b.blockCode} — {b.blockName}
                      </Typography>
                      {b.development && (
                        <Typography variant="caption" color="text.secondary">
                          {b.development}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={3}>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                          Milestones
                        </Typography>
                        <Typography fontWeight={700}>{b.milestones}</Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                          Delayed
                        </Typography>
                        <Typography
                          fontWeight={700}
                          color={
                            b.delayedMilestones > 0 ? "#f57c00" : "text.primary"
                          }
                        >
                          {b.delayedMilestones}
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                          Open Snags
                        </Typography>
                        <Typography
                          fontWeight={700}
                          color={b.openSnags > 0 ? "#e65100" : "text.primary"}
                        >
                          {b.openSnags}
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                          Keys Handed
                        </Typography>
                        <Typography fontWeight={700}>
                          {b.keysHanded}/{b.handovers}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LinearProgress
                      variant="determinate"
                      value={b.milestoneProgress}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": { bgcolor: NAVY },
                      }}
                    />
                    <Typography variant="caption" fontWeight={700} color={NAVY}>
                      {b.milestoneProgress}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}

      {blockSummary.length === 0 && (
        <Box
          textAlign="center"
          py={8}
          color="text.secondary"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
        >
          <AccountTree sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography>
            No project blocks yet. Add milestones, snags or handovers to get
            started.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
